import type { FC } from 'react';
import { Fragment, useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { useRouteLoaderData } from '@remix-run/react';
import cn from 'classnames';

import { FEED_PANEL_ID, FEED_VIEW_DEFAULT, type FeedView, GATE_SCOPES, type GateScope } from '~/constants/community';
import { useIntersectionObserver } from '~/hooks/useIntersectionObserver';
import type { Community, CommunityPost, CommunitySpace, PaginationMeta } from '~/interfaces/community';
import type { RootLoaderData } from '~/root';
import { NectarPageBlock } from '~/utils/nectarPageBlock';

import { PostHistoryNotice } from '../ui/PostHistoryNotice';

import { useCommunityLogoUrl } from '../../_hooks/useCommunityLogoUrl';
import { useCommunityMembership } from '../../_hooks/useCommunityMembership';
import { useCommunityPostOptimistic } from '../../_hooks/useCommunityPostOptimistic';
import { useCommunityPosts } from '../../_hooks/useCommunityPosts';
import { useFeedView } from '../../_hooks/useFeedView';
import { usePinPostFetcher } from '../../_hooks/usePinPostFetcher';
import { useSpace } from '../../_hooks/useSpace';
import { useSpaceAccess } from '../../_hooks/useSpaceAccess';
import { PostInput } from '../Composer/PostInput';
import { GatedCommunityOverlay } from '../GatedCommunityOverlay';
import { GatedSpaceOverlay } from '../GatedSpaceOverlay';
import InstallAppCallout from '../InstallAppCallout';
import { PostCard } from '../PostCard';
import { PostCardSkeleton } from '../PostCard/PostCardSkeleton';
import { usePostComposer } from '../PostComposer';
import { EmptyState } from './_components/EmptyState';
import { FeedTabBar } from './_components/FeedTabBar';
import { HiddenFromMembersNotice } from './_components/HiddenFromMembersNotice';
import { PinnedPosts } from './_components/PinnedPosts';
import { SpaceHeader } from './_components/SpaceHeader';

interface CommunityFeedProps {
  initialPosts: CommunityPost[];
  initialMeta: PaginationMeta;
  pinnedPosts?: CommunityPost[];
  isLoading?: boolean;
}

interface CommunityLayoutData {
  community: Community | null;
  spaces: CommunitySpace[];
}

export const CommunityFeed: FC<CommunityFeedProps> = ({
  initialPosts,
  initialMeta,
  pinnedPosts = [],
  isLoading = false,
}) => {
  const { t } = useTranslation();
  const { openComposer } = usePostComposer();
  const { activeView, setFeedView } = useFeedView();
  const { isMember, openJoinModal } = useCommunityMembership();
  const { canPostIn, canPostAnywhere } = useSpaceAccess();
  const space = useSpace();
  // Space (channel) view ignores the global feed selector
  const view: FeedView = space ? FEED_VIEW_DEFAULT : activeView;
  // Impression surface: a space (channel) view vs the aggregated feed.
  const pageBlock: NectarPageBlock = space ? NectarPageBlock.CommunitySpaceFeed : NectarPageBlock.CommunityFeed;
  const gateScope: GateScope = space ? GATE_SCOPES.Channel : GATE_SCOPES.Community;

  const handleViewChange = useCallback(
    (next: FeedView) => {
      if (!isMember && next === 'following') {
        openJoinModal(NectarPageBlock.CommunityFeedFollowingTab);
        return;
      }
      setFeedView(next);
    },
    [isMember, openJoinModal, setFeedView],
  );

  const {
    posts,
    olderPostsHidden,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isError,
    isLoading: isQueryLoading,
  } = useCommunityPosts({
    spaceSlug: space?.slug,
    view,
    initialPosts,
    initialMeta,
  });
  const sentinelRef = useRef<HTMLDivElement>(null);
  const topComposerRef = useRef<HTMLDivElement>(null);
  const sentinelEntry = useIntersectionObserver(sentinelRef, { rootMargin: '400px' });
  const topComposerEntry = useIntersectionObserver(topComposerRef, { threshold: 0 });
  const { mergePost } = useCommunityPostOptimistic();
  const isTopComposerVisible = topComposerEntry?.isIntersecting ?? true;

  useEffect(() => {
    if (sentinelEntry?.isIntersecting && hasNextPage && !isFetchingNextPage && !isError) {
      fetchNextPage();
    }
  }, [sentinelEntry?.isIntersecting, hasNextPage, isFetchingNextPage, isError, fetchNextPage]);

  usePinPostFetcher();

  const rootData = useRouteLoaderData('root') as RootLoaderData | undefined;
  const layoutData = useRouteLoaderData('routes/community') as CommunityLayoutData | undefined;
  const community = layoutData?.community ?? null;
  const communityLogoUrl = useCommunityLogoUrl();
  const communityPwaBranding =
    community?.pwa_enabled && communityLogoUrl
      ? {
          name: community.name,
          logoUrl: communityLogoUrl,
          subtitle: rootData?.site?.publication?.name,
        }
      : undefined;

  const hasPosts = posts.length > 0 || pinnedPosts.length > 0;
  const firstHiddenPostId = posts.find((post) => post.hidden_from_members)?.id;
  const canPostHere = space ? canPostIn(space) : canPostAnywhere;
  const showComposer = hasPosts && canPostHere;
  const isFollowingView = view === 'following';
  const handleCreatePost = (spaceSlug?: string) =>
    openComposer(spaceSlug ? { defaultSpaceSlug: spaceSlug } : undefined);

  return (
    <div
      className={cn(
        'flex flex-col items-center w-full',
        !showComposer && isMember && 'min-h-full',
        !isMember && 'h-full overflow-hidden',
      )}
    >
      {space ? (
        <SpaceHeader />
      ) : (
        <FeedTabBar activeTab={activeView} onTabChange={handleViewChange} variant="sort-trigger" />
      )}
      <div
        id={FEED_PANEL_ID}
        role="tabpanel"
        className={cn(
          'w-full max-w-[704px] flex flex-col gap-6 px-4 lg:px-0 min-h-0',
          !showComposer && 'flex-1',
          showComposer ? 'pb-28' : 'pb-6',
        )}
      >
        {space?.description && (
          <p className="text-balance text-center text-sm leading-5 text-black/60">{space.description}</p>
        )}
        {communityPwaBranding ? <InstallAppCallout branding={communityPwaBranding} /> : null}
        {canPostHere && (
          <div ref={topComposerRef}>{hasPosts && <PostInput placement="inline" spaceSlug={space?.slug} />}</div>
        )}
        {/* Outer (community) overlay wins precedence; inner (space)
            overlay short-circuits when community is also inaccessible. */}
        <GatedCommunityOverlay scope={gateScope}>
          <GatedSpaceOverlay scope={gateScope} space={space}>
            <PinnedPosts pinnedPosts={pinnedPosts} pageBlock={pageBlock} />
            {isLoading || isQueryLoading ? (
              <PostCardSkeleton />
            ) : hasPosts ? (
              <>
                {posts.map((post) => (
                  <Fragment key={post.id}>
                    {post.id === firstHiddenPostId && <HiddenFromMembersNotice />}
                    <PostCard post={mergePost(post)} hideSpace={!!space} pageBlock={pageBlock} />
                  </Fragment>
                ))}
                {hasNextPage && !isError && <div ref={sentinelRef} className="h-1" />}
                {isFetchingNextPage && <PostCardSkeleton />}
                {isError && (
                  <div className="flex justify-center py-8">
                    <button
                      type="button"
                      className="text-xs text-wt-text-secondary hover:text-wt-text-primary"
                      onClick={() => fetchNextPage()}
                    >
                      {t('community.feed.load_more_retry')}
                    </button>
                  </div>
                )}
              </>
            ) : space ? (
              <EmptyState
                heading={
                  olderPostsHidden ? t('community.feed.empty_recent_heading') : t('community.space.empty_heading')
                }
                subtext={
                  olderPostsHidden ? t('community.space.empty_recent_subtext') : t('community.space.empty_subtext')
                }
                showCreatePost={canPostHere}
                onCreatePost={() => handleCreatePost(space.slug)}
              />
            ) : isFollowingView ? (
              <EmptyState
                heading={
                  olderPostsHidden
                    ? t('community.feed.empty_recent_heading')
                    : t('community.feed.empty_following_heading')
                }
                subtext={
                  olderPostsHidden
                    ? t('community.feed.empty_following_recent_subtext')
                    : t('community.feed.empty_following_subtext')
                }
              />
            ) : (
              <EmptyState
                heading={
                  olderPostsHidden ? t('community.feed.empty_recent_heading') : t('community.feed.empty_heading')
                }
                subtext={t('community.feed.empty_subtext')}
                showCreatePost={canPostHere}
                onCreatePost={() => handleCreatePost()}
              />
            )}
            {olderPostsHidden && <PostHistoryNotice />}
          </GatedSpaceOverlay>
        </GatedCommunityOverlay>
      </div>
      {showComposer && <PostInput placement="sticky" isVisible={!isTopComposerVisible} spaceSlug={space?.slug} />}
    </div>
  );
};
